import FormattedMessage from 'I18n/components/FormattedMessage';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { SuccessIcon } from 'foundations-assets';
import { Small } from 'foundations-components/transitional/components/Small';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getExactKeyPath, isLogicGroup } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import { isConditionWithErrors } from '../../filterQueryFormat/logic/LogicGroup';
import debounce from 'transmute/debounce';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const EDIT_STATUSES = Object.freeze({
  NO_CHANGES: undefined,
  SAVING_CHANGES: 'SAVING_CHANGES',
  CHANGES_SAVED: 'CHANGES_SAVED'
});
const EditorTitle = () => {
  const {
    conditionPathToChangeFieldFor,
    draftCondition,
    internalDraftValue,
    isFieldSelectMode,
    props: {
      uiMessages: {
        editorAddFilterHeaderLabel,
        editorEditFilterHeaderLabel,
        editorChangeFilterHeaderLabel
      }
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const [editStatus, setEditStatus] = useState(EDIT_STATUSES.NO_CHANGES);
  const condition = draftCondition && !draftCondition.isAddingNew ? getExactKeyPath(draftCondition.conditionPath, internalDraftValue) : undefined;
  const hasValidationErrors = condition && !isLogicGroup(condition) ? isConditionWithErrors(condition) : false;
  const prevDraftConditionRef = useRef({
    condition,
    conditionPath: draftCondition === null || draftCondition === void 0 ? void 0 : draftCondition.conditionPath
  });
  const timeoutIdsRef = useRef([]);
  const triggerFakeSavingChanges = useMemo(() => debounce(500, () => {
    setEditStatus(EDIT_STATUSES.SAVING_CHANGES);
    timeoutIdsRef.current.push(setTimeout(() => {
      setEditStatus(EDIT_STATUSES.CHANGES_SAVED);
      timeoutIdsRef.current.push(setTimeout(() => {
        setEditStatus(EDIT_STATUSES.NO_CHANGES);
      }, 2000));
    }, 500));
  }), []);
  useEffect(() => {
    // when switched to another group show "no changes" message
    if (prevDraftConditionRef.current.conditionPath && !(draftCondition !== null && draftCondition !== void 0 && draftCondition.conditionPath.equals(prevDraftConditionRef.current.conditionPath))) {
      setEditStatus(EDIT_STATUSES.NO_CHANGES);
      prevDraftConditionRef.current = {
        condition,
        conditionPath: draftCondition === null || draftCondition === void 0 ? void 0 : draftCondition.conditionPath
      };
      return undefined;
    }

    // when new changes are made to the opened condition and it is valid
    // trigger "saving changes" message
    if (prevDraftConditionRef.current.condition && prevDraftConditionRef.current.condition !== condition && !hasValidationErrors) {
      triggerFakeSavingChanges();
      prevDraftConditionRef.current.condition = condition;
    }
    const timeoutIds = timeoutIdsRef.current;
    return () => {
      while (timeoutIds.length) {
        clearTimeout(timeoutIds.pop());
      }
    };
  }, [condition, draftCondition === null || draftCondition === void 0 ? void 0 : draftCondition.conditionPath, hasValidationErrors, triggerFakeSavingChanges]);
  if (conditionPathToChangeFieldFor) {
    return /*#__PURE__*/_jsx(H5, {
      marginBlockEnd: "0",
      children: editorChangeFilterHeaderLabel || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "newCustomerDataFilters.filterGroupEditor.editorPanelTitleChangeFilter"
      })
    });
  } else if (isFieldSelectMode) {
    return /*#__PURE__*/_jsx(H5, {
      marginBlockEnd: "0",
      children: editorAddFilterHeaderLabel || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "newCustomerDataFilters.filterGroupEditor.editorPanelTitleAddFilter"
      })
    });
  } else if (editStatus === EDIT_STATUSES.SAVING_CHANGES) {
    return /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "newCustomerDataFilters.filterGroupEditor.applyingChanges"
        }),
        layout: "inline",
        minHeight: 18,
        showLabel: true,
        size: "xs",
        margin: "0"
      })
    });
  } else if (editStatus === EDIT_STATUSES.CHANGES_SAVED) {
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(IconHolder, {
        color: "fill-positive-default",
        size: "300",
        icon: SuccessIcon
      }), /*#__PURE__*/_jsx(Small, {
        color: "do-not-use-obsidian",
        "data-selenium-test": "changes-saved-msg",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "newCustomerDataFilters.filterGroupEditor.changesApplied"
        })
      })]
    });
  } else {
    return /*#__PURE__*/_jsx(H5, {
      marginBlockEnd: "0",
      children: editorEditFilterHeaderLabel || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "newCustomerDataFilters.filterGroupEditor.editorPanelTitleDefault"
      })
    });
  }
};
export default EditorTitle;